// "Call window" preference: per user, on by default (Allen, October 30-31, 2026). When on, Dial and Answer open the
// call window from the click itself; when off, nothing opens by itself and the dock's "Open call window" button still
// works. Stored as a JSON map keyed like the coach preference (coachUserKey), synced across windows through the
// storage event so the dock and the call window agree. The CRM-page pop (sidetone.contextCard.autoPop) is a separate,
// per-browser toggle in contextCard.ts, off by default since the call window carries the card (October 31, 2026).
import { useCallback, useSyncExternalStore } from 'react';

export const CALL_WINDOW_PREF_KEY = 'sidetone.callWindow.enabled';

type PrefStorage = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): PrefStorage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}

function readMap(storage: PrefStorage | null): Record<string, unknown> {
  const parsed = JSON.parse(storage?.getItem(CALL_WINDOW_PREF_KEY) ?? '{}') as unknown;
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
}

/** On unless this user turned it off. A missing key, unreadable JSON or blocked storage all read as on. */
export function readCallWindowEnabled(storage: PrefStorage | null, userKey: string): boolean {
  try {
    const map = readMap(storage);
    return userKey in map ? map[userKey] !== false : true;
  } catch { return true; }
}

/** Stores an explicit true or false for this user only. False when storage refused the write. */
export function writeCallWindowEnabled(storage: PrefStorage | null, userKey: string, on: boolean): boolean {
  if (!storage) return false;
  try {
    let map: Record<string, unknown>;
    try { map = readMap(storage); } catch { map = {}; }
    storage.setItem(CALL_WINDOW_PREF_KEY, JSON.stringify({ ...map, [userKey]: on }));
    return true;
  } catch { return false; }
}

export interface CallWindowPreference {
  get(userKey: string): boolean;
  set(userKey: string, on: boolean): void;
  subscribe(listener: () => void): () => void;
}

/** One value for every mounted control and the gesture handlers, which read it synchronously at click time. A choice
 * that storage refused is held in memory for this page. */
export function createCallWindowPreference(storage: () => PrefStorage | null = browserStorage): CallWindowPreference {
  const memory = new Map<string, boolean>();
  const listeners = new Set<() => void>();
  const notify = () => { for (const l of [...listeners]) l(); };
  return {
    get: userKey => memory.get(userKey) ?? readCallWindowEnabled(storage(), userKey),
    set: (userKey, on) => {
      if (writeCallWindowEnabled(storage(), userKey, on)) memory.delete(userKey);
      else memory.set(userKey, on);
      notify();
    },
    subscribe: listener => {
      listeners.add(listener);
      const onStorage = (e: StorageEvent) => { if (e.key === CALL_WINDOW_PREF_KEY || e.key === null) listener(); };
      if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(listener);
        if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
      };
    },
  };
}

export const callWindowPreference = createCallWindowPreference();

export function useCallWindowEnabled(userKey: string): [boolean, (on: boolean) => void] {
  const get = useCallback(() => callWindowPreference.get(userKey), [userKey]);
  const enabled = useSyncExternalStore(callWindowPreference.subscribe, get, () => true);
  const set = useCallback((on: boolean) => callWindowPreference.set(userKey, on), [userKey]);
  return [enabled, set];
}
